import React, { useEffect, useMemo, useState } from 'react'
import { Cotizacion } from '../../../../Pages/Auto/Precios'
import { SegCardPriceMobile } from '../../../SegCardPriceMobile/SegCardPriceMobile';
import styled from '@emotion/styled';
import Skeleton from 'react-loading-skeleton';
import { SegSinCotis } from '../../../SegUI/SegSinCotis/SegSinCotis';
import { useConfiguracionEmpresa } from '../../../../Services/Providers/useConfiguracionEmpresa';
import { ButtonContratar } from './ButtonContratar';
import { Link } from 'react-router-dom';
import type { BffError } from '../../../../Services/API/bff/auto/bffError';

interface CardProps {
    cotizaciones: any[];
    setDescripciones: (descripciones: any) => void;
    cotizando: boolean;
    rubro?: string;
    pais?: "AR" | "UY" | "BR";
    /** Solo /retomar: contenido extra en las acciones de cada card (p. ej. cuánto cambió el precio). */
    extraDe?: (cotizacion: any) => React.ReactNode;
    /** Solo /retomar: CONTRATAR deshabilitado mientras los precios están vencidos. */
    contratarDeshabilitado?: boolean;
    /** Solo /retomar: se espera antes de la selección (trae los datos de la persona). */
    antesDeContratar?: (itemId: string) => Promise<boolean>;
    /** Solo /retomar: la selección falló; true = la pantalla lo resuelve y no se navega. */
    alFallarSeleccion?: (error: BffError) => boolean;
}

const numberFromMoney = (v: string | number): number => {
    if (typeof v === "number") return v;
    // quitar símbolos y espacios
    let s = v.replace(/[^\d,.\-]/g, "").trim();

    // Si hay coma y punto, asumimos formato "12.345,67" => usar coma como decimal
    if (s.includes(",") && s.includes(".")) {
        // quitar separador de miles ".", cambiar coma por punto
        s = s.replace(/\./g, "").replace(",", ".");
    } else if ((s.match(/,/g) || []).length === 1 && !s.includes(".")) {
        // sólo coma: tomarla como decimal
        s = s.replace(",", ".");
    } else {
        // sólo puntos o nada: ya está
    }

    const n = Number(s);
    return isNaN(n) ? 0 : n;
};

const sortByPriceAsc = (a: any, b: any) =>
    numberFromMoney(a.premio) - numberFromMoney(b.premio);

const sortByPriceDesc = (a: any, b: any) =>
    numberFromMoney(b.premio) - numberFromMoney(a.premio);

const sortByAseguradoraAsc = (a: any, b: any) =>
    String(a.NombreCompania || "").localeCompare(String(b.NombreCompania || ""));

const parseFiltro = (f: string) => (f ? f.split(",") : []);
const matchesFiltro = (col: string, filtroStr: string) => {
    if (filtroStr === "") return true;
    const arr = parseFiltro(filtroStr);
    return arr.includes(col);
};

export const Cards = ({
    cotizaciones,
    setDescripciones,
    cotizando,
    rubro = "Auto",
    pais = "AR",
    extraDe,
    contratarDeshabilitado = false,
    antesDeContratar,
    alFallarSeleccion,
    ...rest
}: CardProps) => {

    const CardMob = styled.div`
    width: 100%;
    max-width: 600px;
    margin-bottom: 20px;
    text-align: center;
    font-weight: 500;
    font-size: 12px;
    color: #363636;
    background: #fff;
    box-shadow: 0px 0px 4px #DCE0E2;
    border-radius: 5px;
    padding: 8px;
    display: block;
    @media (min-width: 1340px) {
        max-width: 45%;
        margin: 1em 0 1em 0;
    }
    @media (max-width: 1340px) {
        max-width: 48%;
    }
    @media (max-width: 1145px) {
        max-width: 400px;
    }
    @media (max-width: 992px) {
        max-width: 350px;
    }
    @media (max-width: 805px) {
        max-width: 320px;
    }

    @media (max-width: 778px) {
        max-width: 320px;
    }
    @media (max-width: 745px) {
        max-width: 500px;
    }
    @media (max-width: 340px) {
        max-width: 280px;
        margin: 2px 2px 1.5em 2px
    }

`

    const [opcionFiltrado, setOpcionFiltrado] = useState<string>("");
    const [isAseguradoraFilter, setIsAseguradoraFilter] = useState<boolean>(false);
    const [isPrecioFilter, setIsPrecioFilter] = useState<boolean>(false);
    const { config, origen } = useConfiguracionEmpresa();
    const [isLoading, setIsLoading] = useState(false);

    const filtrarCotizaciones = (source: any[], opcionFiltrado: string) => {
        if (!Array.isArray(source)) return [];
        // NO mutar: crear un array nuevo y clonar cada item con la prop Mostrar
        return source.map(objeto => ({
            ...objeto,
            Mostrar: matchesFiltro(objeto.columna, opcionFiltrado),
        }));
    };


    const cotizacionesAjustadas = useMemo(() => {
        if (!Array.isArray(cotizaciones)) return [];

        // 1️⃣ Generar base con flag Mostrar según cobertura
        const base = filtrarCotizaciones(cotizaciones ?? [], opcionFiltrado);

        // 2️⃣ Filtramos solo las visibles
        const visibles = base.filter(c => c.Mostrar);

        // 3️⃣ Ordenamos visibles según filtro activo
        let visiblesOrdenadas = [...visibles];
        if (isPrecioFilter) {
            visiblesOrdenadas.sort(sortByPriceAsc);
        } else if (isAseguradoraFilter) {
            visiblesOrdenadas.sort(sortByAseguradoraAsc);
        }

        // 4️⃣ Devolvemos un nuevo array donde las visibles van primero (ordenadas)
        //    y las no visibles (Mostrar = false) van después (sin afectar orden)
        const noVisibles = base.filter(c => !c.Mostrar);
        return [...visiblesOrdenadas, ...noVisibles];
    }, [cotizaciones, opcionFiltrado, isPrecioFilter, isAseguradoraFilter]);


    useEffect(() => {
        document.querySelectorAll('.tab-pane').forEach((p) => {
            p.id === "tab-9-pane" && p.classList.add('show', 'active');
            p.id === "tab-0-pane" && p.classList.add('show', 'active');
        });
        document.querySelectorAll('.nav-item > .nav-link').forEach((p) => {
            p.id === "tab-0" && p.classList.add('active');
            p.id === "tab-9" && p.classList.add('active');
        });
    }, [])



    const handleDescripcion = (event: React.MouseEvent, coti: Cotizacion) => {
        if (coti) {
            const nuevaDesc: any = {
                id: coti.id,
                amountMonth: coti.premio,
                companyLogo: coti.LogoCompania,
                companyName: coti.NombreCompania,
                codCompany: coti.CodigoCompania,
                descList: coti.descripciones,
                discountAmount: coti.PrecioSinDescuento,
                discountPerc: coti.DescuentoPorcentaje,
                fiscalInfoImg: coti.Afip,
                nombreCompania: coti.NombreCompania,
                planName: coti.nombreCobertura,
                sumaAsegurada: coti.SumaAsegurada,
                vtvRequerida: coti.vtvRequerida
            }
            setDescripciones(nuevaDesc);
        }
    }

    const onChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setOpcionFiltrado(e.target.value)
    }

    const handlePrecioFilterChange = () => {
        setIsPrecioFilter(prev => {
            const next = !prev;
            if (next) setIsAseguradoraFilter(false);
            return next;
        });
    };

    const handleAseguradoraFilterChange = () => {
        setIsAseguradoraFilter(prev => {
            const next = !prev;
            if (next) setIsPrecioFilter(false);
            return next;
        });
    };


    return (
        <>
            <div className="mobile-filter bg-d9d9d9 d-lg-none sticky-top">
                <form className="container py-2">
                    <div className="row g-0 mb-1">
                        <label htmlFor="cboCoberturas" className="col-4 col-form-label">Cobertura</label>
                        <div className="col-8">
                            <div className="col-12">
                                {pais === "AR" ? (
                                    <select onChange={onChangeSelect} value={opcionFiltrado} className="form-select form-select-sm border-0" id="cboCoberturas">
                                        <option value="">Todas las coberturas</option>
                                        <option value="B">Terceros Completo</option>
                                        <option value="C">Terceros Completo Full</option>
                                        <option value="D,E">Todo Riesgo</option>
                                        <option value="A">Responsabilidad civil</option>
                                    </select>
                                ) : (
                                    <select onChange={onChangeSelect} value={opcionFiltrado} 
                                    className="form-select form-select-sm border-0" id="cboCoberturas" 
                                    >
                                        <option value="">Todas las coberturas</option>
                                        <option value="A">Cobertura SOA</option>
                                        <option value="B">Responsabilidad Civil</option>
                                        <option value="C,D">Triple</option>
                                        <option value="E,F">Totales</option>
                                    </select>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center">
                        <label className="col-4 col-form-label">Ordenar Por:</label>
                        <label className="form-check-label fs-14 mx-1" htmlFor="chkAseguradora">Aseguradora</label>
                        <label className="form-switch">
                            <input onChange={handleAseguradoraFilterChange} checked={isAseguradoraFilter} className="form-check-input" type="checkbox" id="chkAseguradora" />
                        </label>
                        <label className="form-check-label fs-14 mx-1" htmlFor="chkPrecio">Precio</label>
                        <label className="form-switch">
                            <input onChange={handlePrecioFilterChange} checked={isPrecioFilter} className="form-check-input" type="checkbox" id="chkPrecio" />
                        </label>
                    </div>
                </form>
            </div>
            <div className="cob d-none d-lg-block sticky-lg-top2">
                {
                    pais === "AR" &&
                    <ul className="nav nav-tabs gap-2" role="tablist">
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-1" onClick={(e) => setOpcionFiltrado("A")} data-bs-toggle="tab" data-bs-target="#tab-1-pane" type="button" role="tab" aria-controls="tab-1-pane" aria-selected="true">Responsabilidad Civil</button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-2" onClick={(e) => setOpcionFiltrado("B")} data-bs-toggle="tab" data-bs-target="#tab-2-pane" type="button" role="tab" aria-controls="tab-2-pane" aria-selected="false">Terceros Completo</button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-3" onClick={(e) => setOpcionFiltrado("C")} data-bs-toggle="tab" data-bs-target="#tab-3-pane" type="button" role="tab" aria-controls="tab-3-pane" aria-selected="false">Terceros Completo Full</button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-4" onClick={(e) => setOpcionFiltrado("D,E")} data-bs-toggle="tab" data-bs-target="#tab-4-pane" type="button" role="tab" aria-controls="tab-4-pane" aria-selected="false">Todo Riesgo</button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-0" onClick={(e) => setOpcionFiltrado("")} data-bs-toggle="tab" data-bs-target="#tab-0-pane" type="button" role="tab" aria-controls="tab-0-pane" aria-selected="false">Todas las coberturas</button>
                        </li>
                    </ul>
                }
                {
                    pais === "UY" &&
                    <ul className="nav nav-tabs gap-2" role="tablist">
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-1" onClick={(e) => setOpcionFiltrado("A")} data-bs-toggle="tab" data-bs-target="#tab-1-pane" type="button" role="tab" aria-controls="tab-1-pane" aria-selected="true">
                                Cobertura SOA
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-2" onClick={(e) => setOpcionFiltrado("B")} data-bs-toggle="tab" data-bs-target="#tab-2-pane" type="button" role="tab" aria-controls="tab-2-pane" aria-selected="false">
                                Responsabilidad Civil
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-3" onClick={(e) => setOpcionFiltrado("C,D")} data-bs-toggle="tab" data-bs-target="#tab-3-pane" type="button" role="tab" aria-controls="tab-3-pane" aria-selected="false">
                                Triple
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-4" onClick={(e) => setOpcionFiltrado("E,F")} data-bs-toggle="tab" data-bs-target="#tab-4-pane" type="button" role="tab" aria-controls="tab-4-pane" aria-selected="false">
                                Totales
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="tab-0" onClick={(e) => setOpcionFiltrado("")} data-bs-toggle="tab" data-bs-target="#tab-0-pane" type="button" role="tab" aria-controls="tab-0-pane" aria-selected="false">
                                Todas las coberturas
                            </button>
                        </li>
                    </ul>
                }

                <div className="tab-content">
                    <div className="tab-pane fade show" id="tab-1-pane" role="tabpanel" aria-labelledby="tab-1" tabIndex={0}>
                        <b>La cobertura incluye:</b>
                        <ul className="d-inline list-inline ms-2">
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Responsabilidad Civil</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Cobertura en Mercosur</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Auxilio (No todas las compañías)</li>
                        </ul>
                    </div>
                    <div className="tab-pane fade" id="tab-2-pane" role="tabpanel" aria-labelledby="tab-2" tabIndex={0}>
                        <b>La cobertura incluye:</b>
                        <ul className="d-inline list-inline ms-2">
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Responsabilidad Civil</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Cobertura en Mercosur</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Auxilio</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Robo/Hurto</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Incendio</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Parciales</li>
                        </ul>
                    </div>
                    <div className="tab-pane fade" id="tab-3-pane" role="tabpanel" aria-labelledby="tab-3" tabIndex={0}>
                        <b>La cobertura incluye:</b>
                        <ul className="d-inline list-inline ms-2">
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Responsabilidad Civil</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Cobertura en Mercosur</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Auxilio</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Robo/Hurto</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Incendio</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Parciales</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Cristales</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Cerraduras</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Granizo</li>
                        </ul>
                    </div>
                    <div className="tab-pane fade" id="tab-4-pane" role="tabpanel" aria-labelledby="tab-4" tabIndex={0}>
                        <b>La cobertura incluye:</b>
                        <ul className="d-inline list-inline ms-2">
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Responsabilidad Civil</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Cobertura en Mercosur</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Auxilio</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Robo/Hurto</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Incendio</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Parciales</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Cristales</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Cerraduras</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Granizo</li>
                            <li className="list-inline-item"><svg className="bi tick-pri"><use xlinkHref="#tick" /></svg> Daños parciales por accidente</li>
                        </ul>
                    </div>
                    <div className="tab-pane fade" id="tab-0-pane" role="tabpanel" aria-labelledby="tab-0" tabIndex={0}>Todas las coberturas</div>
                </div>
            </div>

            <div className="cards2-wrapper">
                {cotizacionesAjustadas && cotizacionesAjustadas.map((coti, i) =>
                    coti && coti.Mostrar &&
                    (<SegCardPriceMobile
                        rubro={rubro}
                        amountMonth={coti.premio}
                        companyLogo={coti.LogoCompania}
                        companyName={coti.NombreCompania}
                        descList={coti.DescResumen}
                        discountAmount={coti.PrecioSinDescuento}
                        discountPerc={coti.DescuentoPorcentaje + "%"}
                        fiscalInfoImg={coti.Afip}
                        planName={coti.nombreCobertura}
                        onClick={(e) => handleDescripcion(e, coti)}
                        configEmpresa={config.origen !== "SEGURARSE" ? config : undefined}
                        key={i}
                        superdescuento={coti.esPrecioPromocional}
                        pricepermonth={pais === "AR"}
                    >
                        <div className={`actions d-flex flex-column ${window.innerWidth > 778 && "pt-3"}`}>
                            {extraDe ? extraDe(coti) : null}
                            <ButtonContratar
                                cotizacion={coti}
                                rubro={rubro}
                                styles={{ width: "100%" }}
                                deshabilitado={contratarDeshabilitado}
                                antesDeContratar={antesDeContratar}
                                alFallarSeleccion={alFallarSeleccion}
                            />
                            <Link
                                onClick={(e: any) => handleDescripcion(e, coti)}
                                data-bs-toggle="offcanvas"
                                to={"#offcanvas"}
                                role="button"
                                className="btn-detalle">
                                {"Ver detalle >"}
                            </Link>
                        </div>
                    </SegCardPriceMobile>)
                )}
                {cotizando ?
                    <CardMob key={"skeleton-cards-"}>
                        <Skeleton
                            containerClassName="flex-1"
                            count={1}
                            style={{ borderRadius: "100%", width: "50px", height: "50px" }} />
                        <Skeleton
                            containerClassName="flex-1"
                            count={3}
                            circle={false}
                            style={{ height: "15px", margin: "10px 0" }}
                        />
                    </CardMob>
                    :
                    (cotizacionesAjustadas.filter(cot => cot.Mostrar).length === 0) ?
                        <SegSinCotis mostrarWpp={origen === "SEGURARSE"} />
                        :
                        <></>
                }
            </div>
        </>
    )
}